/**
 * FieldHelp — kontextueller Erklärungs-Tooltip / Disclosure neben einem
 * Formularfeld-Label.
 *
 * Bewusst KEIN Floating-Tooltip: Auf Mobile sind echte Tooltips eine UX-Falle
 * (Hover existiert nicht, Touch-Trigger ist schwer zu schließen). Stattdessen
 * ein Disclosure-Panel, das in den Lesefluss aufklappt — funktioniert auf
 * 320px wie auf 1920px.
 *
 * Phase A: erweitert um echten Rückfragen-Pfad. Statt eines reinen
 * „Sie können das offen lassen"-Stubs gibt es jetzt zwei Disclosure-Pfade:
 *   1. „Mehr Hilfe" — der bisherige Erklärungs-Inhalt
 *   2. „Frage an den Berater stellen" — Inline-Form mit Textarea, schreibt
 *      via createClarification() in case_clarifications.
 * Der Rückfragen-Pfad ist nur sichtbar, wenn `caseId` als Prop übergeben
 * wird — ohne caseId (z.B. in statischen Erklär-Kontexten) fällt der Pfad
 * silent aus.
 *
 * A11y:
 *   - Trigger ist ein <button> mit `aria-expanded` und `aria-controls`
 *   - Panel hat `role="region"` mit dem Label „Erläuterung"
 *   - ESC schließt, wenn der Trigger oder das Panel den Fokus hat
 *   - Touch-Target ≥ 32×32px (Trigger-Padding sorgt dafür)
 *
 * Pflege: Texte kommen aus `lib/i18n/fieldHelp.ts`. Kein Hardcoding hier.
 */

import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { useParams } from 'react-router-dom';
import { HelpCircle, X } from 'lucide-react';
import { de } from '../../lib/i18n/de';
import { getFieldHelp, type FieldHelpKey } from '../../lib/i18n/fieldHelp';
import { createClarification } from '../../lib/supabaseClarifications';
import { useAuthOrSession } from '../../lib/authOrSession';

interface FieldHelpProps {
  /** Schlüssel im FieldHelp-Repository. Unbekannte Schlüssel rendern null. */
  readonly pathKey: FieldHelpKey;
  /**
   * Optional: zusätzlicher CSS-Klassen-String für den äußeren Wrapper.
   * Erlaubt, den Trigger z.B. inline neben einem Label zu positionieren.
   */
  readonly className?: string;
  /**
   * Optional: menschenlesbares Label für das Feld (z.B. "Umsatz / Monat").
   * Wird beim Erstellen einer Rückfrage als field_label persistiert, damit
   * der Berater das Feld auch ohne tiefes Code-Wissen wiederfindet.
   * Default: pathKey selbst.
   */
  readonly fieldLabel?: string;
}

export function FieldHelp({ pathKey, className, fieldLabel }: FieldHelpProps) {
  const entry = getFieldHelp(pathKey);
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Routing-Param: wir akzeptieren beide URL-Param-Namen aus Berater- und
  // Mandant-Wizard-Pfaden.
  const params = useParams<{ caseId?: string; id?: string }>();
  const caseId = params.caseId ?? params.id ?? '';
  const auth = useAuthOrSession();

  if (!entry) return null;

  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      setIsOpen(false);
      // Fokus zurück auf Trigger, damit Tastatur-User nicht ins Leere fallen.
      triggerRef.current?.focus();
    }
  };

  return (
    <div className={className} onKeyDown={handleKey}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setIsOpen((v) => !v);
        }}
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-label={de.mandant.fieldHelp.toggle}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
      >
        <HelpCircle size={16} aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          id={panelId}
          role="region"
          aria-label={de.mandant.fieldHelp.toggle}
          className="mt-3 rounded-sm border border-stone-200 bg-stone-50 px-4 py-4 text-stone-800 md:px-5 md:py-5"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0 space-y-3">
              <FieldHelpBlock label={de.mandant.fieldHelp.whatLabel}>{entry.what}</FieldHelpBlock>
              <FieldHelpBlock label={de.mandant.fieldHelp.whyLabel}>{entry.why}</FieldHelpBlock>
              <FieldHelpBlock label={de.mandant.fieldHelp.exampleLabel}>
                {entry.example}
              </FieldHelpBlock>
              {entry.dsgvoNote && (
                <p className="mt-3 rounded-sm border-l-2 border-amber-300 bg-amber-50 px-3 py-2 font-serif text-xs italic leading-snug text-amber-900">
                  {entry.dsgvoNote}
                </p>
              )}
              <details className="mt-2 group">
                <summary className="cursor-pointer font-sans text-[11px] font-medium uppercase tracking-widest text-stone-500 hover:text-stone-700">
                  {de.mandant.fieldHelp.askAdvisorTitle}
                </summary>
                <p className="mt-2 font-serif text-sm italic leading-snug text-stone-600">
                  {de.mandant.fieldHelp.askAdvisorBody}
                </p>
                {/* Phase A: echter Pfad — Inline-Form für Rückfrage.
                    Phase B: fieldLabel kommt aus dem zentralen FieldHelp-
                    Repository (entry.label), kein doppeltes Pflegen pro
                    Aufrufer. Explizit übergebener fieldLabel hat Vorrang. */}
                {caseId.length > 0 && !auth.isMock && (
                  <AskAdvisorForm
                    caseId={caseId}
                    fieldPath={pathKey}
                    fieldLabel={fieldLabel ?? entry.label}
                  />
                )}
              </details>
            </div>
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                triggerRef.current?.focus();
              }}
              aria-label={de.mandant.fieldHelp.close}
              className="-mr-1 -mt-1 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldHelpBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="font-sans text-[11px] font-medium uppercase tracking-widest text-stone-500">
        {label}
      </p>
      <p className="mt-1 font-serif text-sm leading-relaxed text-stone-800">{children}</p>
    </div>
  );
}

/**
 * Inline-Form unterhalb des „Ich weiß es nicht"-Disclosures.
 * Erlaubt dem Mandanten, eine konkrete Rückfrage zu diesem Feld an den
 * Berater zu senden. Nach erfolgreichem Submit zeigt sie eine kurze
 * Bestätigung und kollabiert die Form-Felder.
 */
function AskAdvisorForm({
  caseId,
  fieldPath,
  fieldLabel,
}: {
  caseId: string;
  fieldPath: string;
  fieldLabel: string;
}) {
  type State =
    | { kind: 'idle' }
    | { kind: 'open' }
    | { kind: 'sending' }
    | { kind: 'success' }
    | { kind: 'error'; message: string };

  const [state, setState] = useState<State>({ kind: 'idle' });
  const [question, setQuestion] = useState('');

  const submit = async () => {
    setState({ kind: 'sending' });
    try {
      await createClarification({ caseId, fieldPath, fieldLabel, question });
      setState({ kind: 'success' });
      setQuestion('');
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : de.mandant.fieldHelp.askAdvisorError,
      });
    }
  };

  if (state.kind === 'success') {
    return (
      <p className="mt-3 rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 font-serif text-sm text-emerald-900">
        {de.mandant.fieldHelp.askAdvisorSuccess}
      </p>
    );
  }

  if (state.kind === 'idle') {
    return (
      <button
        type="button"
        onClick={() => {
          setState({ kind: 'open' });
        }}
        className="mt-3 inline-flex items-center font-sans text-[11px] font-medium uppercase tracking-widest text-stone-700 underline-offset-2 hover:text-stone-900 hover:underline"
      >
        {de.mandant.fieldHelp.askAdvisorAction}
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-sm border border-stone-200 bg-white px-3 py-3">
      <label className="font-sans text-[11px] font-medium uppercase tracking-widest text-stone-500">
        {de.mandant.fieldHelp.askAdvisorFormTitle}
      </label>
      <textarea
        value={question}
        onChange={(e) => {
          setQuestion(e.target.value);
        }}
        placeholder={de.mandant.fieldHelp.askAdvisorFormPlaceholder}
        rows={3}
        className="mt-2 w-full rounded-sm border border-stone-300 bg-white px-3 py-2 font-serif text-sm text-stone-900 placeholder:text-stone-400 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
      />
      {state.kind === 'error' && (
        <p className="mt-2 font-serif text-xs text-rose-700">{state.message}</p>
      )}
      <div className="mt-2 flex flex-col items-stretch gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={() => {
            setState({ kind: 'idle' });
            setQuestion('');
          }}
          className="inline-flex min-h-[36px] items-center justify-center rounded-sm border border-stone-200 bg-white px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest text-stone-700 hover:bg-stone-50"
        >
          {de.mandant.fieldHelp.askAdvisorCancel}
        </button>
        <button
          type="button"
          disabled={state.kind === 'sending' || question.trim().length === 0}
          onClick={() => {
            void submit();
          }}
          className="inline-flex min-h-[36px] items-center justify-center rounded-sm bg-stone-900 px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest text-white hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-300"
        >
          {state.kind === 'sending'
            ? de.mandant.fieldHelp.askAdvisorSubmitting
            : de.mandant.fieldHelp.askAdvisorSubmit}
        </button>
      </div>
    </div>
  );
}
